import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fmtCurrency, fmtDate, fmtTime, statusLabel, MATERIAL_CATALOG } from '../lib/utils';
import { Card, Badge, ProgressBar, Avatar, Btn, Modal, Field, Input, Select, Textarea, SH, Stat, Grid4, Spinner, Toast, B } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const TABS_ADMIN    = ['Overview','Timeline','Materials','Hours','Invoices','Photos','Messages'];
const TABS_CUSTOMER = ['Overview','Timeline','Photos','Messages','Invoices'];

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user, profile } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from('jobs').select('*').eq('id', id).single();
    setJob(data);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function updateJob(patch) {
    const { data, error } = await supabase.from('jobs').update(patch).eq('id', id).select().single();
    if (!error) setJob(data);
    return { data, error };
  }

  function showToast(msg, type = 'success') { setToast({ msg, type }); }

  if (loading) return <Spinner />;
  if (!job) return <div style={{ padding: '2rem', color: '#94A3B8' }}>Job not found.</div>;

  const tabs = isAdmin ? TABS_ADMIN : TABS_CUSTOMER;

  return (
    <div style={{ padding: '1.5rem', fontFamily: "'Barlow',sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <Btn size="sm" variant="outline" onClick={() => navigate(isAdmin ? '/jobs' : '/my-projects')}>← Back</Btn>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, color: '#94A3B8', fontSize: 13 }}>{job.id?.slice(0,8)}</span>
            <h1 style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif", color: B.navy, margin: 0 }}>{job.customer_name} — {job.type}</h1>
            <Badge status={job.status} />
          </div>
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>📍 {job.address}</div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Select value={job.status} onChange={e => updateJob({ status: e.target.value }).then(() => showToast('Status updated.'))} style={{ width: 'auto', fontSize: 12, padding: '5px 8px' }}>
              {['scheduled','in-progress','final-inspection','completed','on-hold','cancelled'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </Select>
          </div>
        )}
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
        <span style={{ fontSize: 12, color: '#94A3B8', minWidth: 68 }}>Progress</span>
        <div style={{ flex: 1 }}><ProgressBar value={job.progress || 0} height={8} /></div>
        <span style={{ fontSize: 14, fontWeight: 800, color: B.navy, minWidth: 38 }}>{job.progress || 0}%</span>
        {isAdmin && <input type="range" min={0} max={100} step={5} value={job.progress || 0} onChange={e => updateJob({ progress: +e.target.value })} style={{ width: 80 }} />}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: '1.5rem', borderBottom: '1px solid #E2E8F0', paddingBottom: '1rem' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t.toLowerCase())}
            style={{ fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, border: 'none', background: tab === t.toLowerCase() ? B.navy : 'transparent', color: tab === t.toLowerCase() ? '#fff' : '#64748B', cursor: 'pointer', fontFamily: "'Barlow',sans-serif", transition: 'all .15s' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview'   && <OverviewTab   job={job} isAdmin={isAdmin} updateJob={updateJob} showToast={showToast} />}
      {tab === 'timeline'   && <TimelineTab   job={job} isAdmin={isAdmin} updateJob={updateJob} showToast={showToast} />}
      {tab === 'materials'  && <MaterialsTab  job={job} isAdmin={isAdmin} updateJob={updateJob} showToast={showToast} />}
      {tab === 'hours'      && <HoursTab      job={job} isAdmin={isAdmin} updateJob={updateJob} showToast={showToast} />}
      {tab === 'invoices'   && <InvoicesTab   job={job} isAdmin={isAdmin} updateJob={updateJob} showToast={showToast} />}
      {tab === 'photos'     && <PhotosTab     job={job} isAdmin={isAdmin} updateJob={updateJob} showToast={showToast} />}
      {tab === 'messages'   && <MessagesTab   job={job} isAdmin={isAdmin} user={user} profile={profile} updateJob={updateJob} showToast={showToast} />}

      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

// ─── OVERVIEW ────────────────────────────────────────────────────────────────
function OverviewTab({ job, isAdmin, updateJob, showToast }) {
  const phases = job.phases || [];
  const curr = phases.find(p => p.current && !p.done) || phases.find(p => !p.done);
  const doneCount = phases.filter(p => p.done).length;
  const totH = (job.hours || []).reduce((s, h) => s + (h.hours || 0), 0);
  const matC = (job.materials || []).reduce((s, m) => s + (m.qty || 0) * (m.unit_cost || 0), 0);
  const laborC = totH * (job.labor_rate || 65);
  const gross = (job.contract_total || 0) - matC - laborC;
  const margin = job.contract_total > 0 ? Math.round(gross / job.contract_total * 100) : 0;
  const bal = (job.contract_total || 0) - (job.amount_paid || 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
      <Card>
        <SH title="Job Details" />
        {[['Customer', job.customer_name], ['Address', job.address], ['Type', job.type], ['Material', job.material], ['Sq Footage', job.sq_footage ? job.sq_footage.toLocaleString() + ' sq ft' : '—'], ['Start Date', fmtDate(job.start_date)], ['Est. Complete', fmtDate(job.est_complete)]].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F5F9', fontSize: 13 }}>
            <span style={{ color: '#94A3B8' }}>{k}</span>
            <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '55%' }}>{v || '—'}</span>
          </div>
        ))}
        {job.notes && <div style={{ marginTop: 10, padding: '10px 12px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A', fontSize: 12, color: '#78350F' }}>📝 {job.notes}</div>}
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Card>
          <SH title="Financials" />
          {[['Contract Total', fmtCurrency(job.contract_total), B.navy], ['Collected', fmtCurrency(job.amount_paid), '#16A34A'], ['Balance Due', fmtCurrency(bal), bal > 0 ? '#D97706' : '#16A34A']].map(([k, v, c]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F1F5F9', fontSize: 13 }}>
              <span style={{ color: '#94A3B8' }}>{k}</span>
              <span style={{ fontWeight: 700, color: c }}>{v}</span>
            </div>
          ))}
          {isAdmin && (
            <>
              <div style={{ height: 1, background: '#E2E8F0', margin: '10px 0' }} />
              {[['Materials', fmtCurrency(matC), '#DC2626'], [`Labor (${totH}h)`, fmtCurrency(laborC), '#D97706'], ['Est. Gross Profit', fmtCurrency(gross), gross >= 0 ? '#16A34A' : '#DC2626'], ['Margin', margin + '%', margin >= 30 ? '#16A34A' : '#D97706']].map(([k, v, c]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12 }}>
                  <span style={{ color: '#94A3B8' }}>{k}</span>
                  <span style={{ fontWeight: 700, color: c }}>{v}</span>
                </div>
              ))}
            </>
          )}
        </Card>
        <Card>
          <SH title="Current Phase" />
          {curr ? (
            <div style={{ padding: '10px 12px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A' }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🔨 {curr.label}</div>
              {curr.notes && <div style={{ fontSize: 12, color: '#78350F' }}>{curr.notes}</div>}
              {curr.date && <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>📅 {fmtDate(curr.date)}</div>}
            </div>
          ) : <div style={{ fontSize: 13, color: '#94A3B8' }}>All phases complete ✓</div>}
          <div style={{ marginTop: 10, fontSize: 12, color: '#94A3B8', marginBottom: 4 }}>{doneCount} of {phases.length} phases complete</div>
          <ProgressBar value={phases.length ? Math.round(doneCount / phases.length * 100) : 0} height={4} />
        </Card>
      </div>
    </div>
  );
}

// ─── TIMELINE ────────────────────────────────────────────────────────────────
function TimelineTab({ job, isAdmin, updateJob, showToast }) {
  const [editing, setEditing] = useState(null);
  const [ed, setEd] = useState({});
  const phases = job.phases || [];

  function markDone(idx) {
    const updated = phases.map((p, i) => {
      if (i === idx) return { ...p, done: true, current: false, completed_date: new Date().toISOString().slice(0, 10) };
      if (i === idx + 1) return { ...p, current: true };
      return p;
    });
    const done = updated.filter(p => p.done).length;
    updateJob({ phases: updated, progress: Math.round(done / updated.length * 100) }).then(() => showToast('Phase complete! ✓'));
  }

  function markUndone(idx) {
    const updated = phases.map((p, i) => i === idx ? { ...p, done: false, completed_date: '' } : p);
    updateJob({ phases: updated }).then(() => showToast('Phase reopened.'));
  }

  function saveEdit(idx) {
    const updated = phases.map((p, i) => i === idx ? { ...p, ...ed } : p);
    updateJob({ phases: updated }).then(() => { showToast('Phase saved.'); setEditing(null); });
  }

  return (
    <Card>
      <SH title="Project Timeline" />
      {phases.map((ph, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: i < phases.length - 1 ? 18 : 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24, flexShrink: 0 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: ph.done ? '#16A34A' : ph.current ? B.amber : '#E2E8F0', border: `2px solid ${ph.done ? '#16A34A' : ph.current ? B.amber : '#CBD5E1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: ph.done || ph.current ? '#fff' : '#94A3B8', fontWeight: 700 }}>
              {ph.done ? '✓' : ph.current ? '●' : i + 1}
            </div>
            {i < phases.length - 1 && <div style={{ flex: 1, width: 2, background: ph.done ? 'rgba(22,163,74,.3)' : '#E2E8F0', marginTop: 3 }} />}
          </div>
          <div style={{ flex: 1, paddingBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: ph.done ? '#16A34A' : ph.current ? B.navy : '#94A3B8' }}>{ph.label}</span>
              {ph.current && !ph.done && <span style={{ fontSize: 9, fontWeight: 700, background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', padding: '1px 7px', borderRadius: 20 }}>IN PROGRESS</span>}
              {ph.done && ph.completed_date && <span style={{ fontSize: 11, color: '#94A3B8' }}>✓ {fmtDate(ph.completed_date)}</span>}
              {!ph.done && ph.date && <span style={{ fontSize: 11, color: '#94A3B8' }}>📅 {fmtDate(ph.date)}</span>}
              {isAdmin && (
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {!ph.done && <Btn size="sm" variant="success" onClick={() => markDone(i)}>Mark done</Btn>}
                  {ph.done && <Btn size="sm" variant="ghost" onClick={() => markUndone(i)}>Undo</Btn>}
                  <Btn size="sm" variant="outline" onClick={() => { setEditing(i); setEd({ date: ph.date || '', completed_date: ph.completed_date || '', notes: ph.notes || '' }); }}>Edit</Btn>
                </span>
              )}
            </div>
            {ph.notes && <div style={{ fontSize: 12, color: '#64748B', background: '#F8FAFC', padding: '5px 8px', borderRadius: 6, marginBottom: 4 }}>{ph.notes}</div>}
            {editing === i && (
              <div style={{ marginTop: 8, padding: 12, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <Field label="Scheduled"><Input type="date" value={ed.date} onChange={e => setEd(d => ({ ...d, date: e.target.value }))} /></Field>
                  <Field label="Completed"><Input type="date" value={ed.completed_date} onChange={e => setEd(d => ({ ...d, completed_date: e.target.value }))} /></Field>
                </div>
                <Field label="Notes"><Textarea value={ed.notes} onChange={e => setEd(d => ({ ...d, notes: e.target.value }))} style={{ minHeight: 60 }} /></Field>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn size="sm" variant="primary" onClick={() => saveEdit(i)}>Save</Btn>
                  <Btn size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </Card>
  );
}

// ─── MATERIALS ───────────────────────────────────────────────────────────────
function MaterialsTab({ job, isAdmin, updateJob, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ catalogId: '', name: '', qty: '', unit: '', unit_cost: '', status: 'not-ordered' });
  const materials = job.materials || [];
  const total = materials.reduce((s, m) => s + (m.qty || 0) * (m.unit_cost || 0), 0);
  const catGroups = MATERIAL_CATALOG.reduce((acc, m) => { (acc[m.category] = acc[m.category] || []).push(m); return acc; }, {});

  function fromCatalog(id) {
    const item = MATERIAL_CATALOG.find(m => m.id === id);
    if (item) setForm(f => ({ ...f, catalogId: id, name: item.name, unit: item.unit, unit_cost: item.unitCost }));
  }

  function add() {
    if (!form.name || !form.qty) { showToast('Name and qty required.', 'error'); return; }
    const updated = [...materials, { ...form, qty: +form.qty, unit_cost: +form.unit_cost }];
    updateJob({ materials: updated }).then(() => { showToast('Material added.'); setShowAdd(false); setForm({ catalogId: '', name: '', qty: '', unit: '', unit_cost: '', status: 'not-ordered' }); });
  }

  function setStatus(idx, status) {
    updateJob({ materials: materials.map((m, i) => i === idx ? { ...m, status } : m) }).then(() => showToast('Status updated.'));
  }

  function remove(idx) {
    if (!window.confirm('Remove this material?')) return;
    updateJob({ materials: materials.filter((_, i) => i !== idx) }).then(() => showToast('Removed.'));
  }

  const matStatus = { delivered: { color: '#16A34A', label: 'Delivered' }, ordered: { color: '#2563EB', label: 'Ordered' }, 'not-ordered': { color: '#94A3B8', label: 'Not Ordered' } };

  return (
    <div>
      <Grid4 style={{ marginBottom: '1rem' }}>
        <Stat label="Total Items" value={materials.length} />
        <Stat label="Delivered" value={materials.filter(m => m.status === 'delivered').length} accent="#16A34A" />
        <Stat label="On Order" value={materials.filter(m => m.status === 'ordered').length} accent={B.info} />
        <Stat label="Total Cost" value={fmtCurrency(total)} accent={B.navy} />
      </Grid4>
      <Card>
        <SH title="Material List" action={isAdmin && <Btn size="sm" variant="primary" onClick={() => setShowAdd(!showAdd)}>+ Add</Btn>} />
        {showAdd && (
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
              <Field label="From Catalog"><Select value={form.catalogId} onChange={e => fromCatalog(e.target.value)}><option value="">— or enter custom —</option>{Object.entries(catGroups).map(([cat, items]) => <optgroup key={cat} label={cat}>{items.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</optgroup>)}</Select></Field>
              <Field label="Name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
              <Field label="Qty *"><Input type="number" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} /></Field>
              <Field label="Unit"><Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="sq, bundle, ft…" /></Field>
              <Field label="Unit Cost ($)"><Input type="number" value={form.unit_cost} onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))} /></Field>
              <Field label="Status"><Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}><option value="not-ordered">Not Ordered</option><option value="ordered">Ordered</option><option value="delivered">Delivered</option></Select></Field>
            </div>
            <div style={{ display: 'flex', gap: 6 }}><Btn size="sm" variant="primary" onClick={add}>Add</Btn><Btn size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn></div>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Material','Qty','Unit','Unit Cost','Total','Status',isAdmin?'':''].filter(Boolean).map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>{h}</th>)}</tr></thead>
            <tbody>
              {materials.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}>No materials yet.</td></tr>}
              {materials.map((m, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '9px 10px', fontWeight: 500 }}>{m.name}</td>
                  <td style={{ padding: '9px 10px', fontWeight: 700 }}>{m.qty}</td>
                  <td style={{ padding: '9px 10px', color: '#94A3B8' }}>{m.unit}</td>
                  <td style={{ padding: '9px 10px' }}>{m.unit_cost ? fmtCurrency(m.unit_cost) : '—'}</td>
                  <td style={{ padding: '9px 10px', fontWeight: 700 }}>{m.unit_cost ? fmtCurrency(m.qty * m.unit_cost) : '—'}</td>
                  <td style={{ padding: '9px 10px' }}>
                    {isAdmin
                      ? <Select value={m.status} onChange={e => setStatus(i, e.target.value)} style={{ fontSize: 11, padding: '3px 6px', color: matStatus[m.status]?.color, fontWeight: 700, width: 'auto' }}>
                          {['not-ordered','ordered','delivered'].map(s => <option key={s} value={s}>{matStatus[s]?.label}</option>)}
                        </Select>
                      : <span style={{ fontSize: 11, fontWeight: 700, color: matStatus[m.status]?.color }}>{matStatus[m.status]?.label}</span>
                    }
                  </td>
                  {isAdmin && <td style={{ padding: '9px 10px' }}><Btn size="sm" variant="danger" onClick={() => remove(i)}>✕</Btn></td>}
                </tr>
              ))}
            </tbody>
            <tfoot><tr style={{ background: '#F8FAFC' }}><td colSpan={4} style={{ padding: '9px 10px', fontWeight: 700, borderTop: '2px solid #E2E8F0' }}>Total</td><td colSpan={isAdmin ? 3 : 2} style={{ padding: '9px 10px', fontWeight: 800, fontSize: 15, borderTop: '2px solid #E2E8F0' }}>{fmtCurrency(total)}</td></tr></tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── HOURS ───────────────────────────────────────────────────────────────────
function HoursTab({ job, isAdmin, updateJob, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), name: '', hours: '', phase: '' });
  const hours = job.hours || [];
  const totH = hours.reduce((s, h) => s + (h.hours || 0), 0);
  const byPerson = hours.reduce((acc, h) => { acc[h.name] = (acc[h.name] || 0) + h.hours; return acc; }, {});

  function add() {
    if (!form.name || !form.hours) { showToast('Name and hours required.', 'error'); return; }
    const updated = [...hours, { ...form, hours: +form.hours }];
    updateJob({ hours: updated }).then(() => { showToast('Hours logged.'); setShowAdd(false); setForm(f => ({ ...f, hours: '', phase: '' })); });
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.25rem' }}>
      <Card>
        <SH title="Hours Log" action={isAdmin && <Btn size="sm" variant="primary" onClick={() => setShowAdd(!showAdd)}>+ Log</Btn>} />
        {showAdd && (
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
              <Field label="Date"><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></Field>
              <Field label="Crew Member *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" /></Field>
              <Field label="Hours *"><Input type="number" step="0.5" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} /></Field>
              <Field label="Phase / Task"><Input value={form.phase} onChange={e => setForm(f => ({ ...f, phase: e.target.value }))} placeholder="e.g. Shingles" /></Field>
            </div>
            <div style={{ display: 'flex', gap: 6 }}><Btn size="sm" variant="primary" onClick={add}>Log</Btn><Btn size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn></div>
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['Date','Crew','Hours','Phase'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>{h}</th>)}</tr></thead>
          <tbody>
            {hours.length === 0 && <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}>No hours logged.</td></tr>}
            {[...hours].sort((a, b) => a.date > b.date ? -1 : 1).map((h, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                <td style={{ padding: '9px 10px' }}>{fmtDate(h.date)}</td>
                <td style={{ padding: '9px 10px', fontWeight: 500 }}>{h.name}</td>
                <td style={{ padding: '9px 10px', fontWeight: 700 }}>{h.hours}h</td>
                <td style={{ padding: '9px 10px', color: '#64748B' }}>{h.phase}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card>
        <SH title="Summary" />
        <Stat label="Total Hours" value={totH + 'h'} sub={`~${fmtCurrency(totH * (job.labor_rate || 65))} est. labor`} accent={B.navy} />
        <div style={{ marginTop: '1rem' }}>
          {Object.entries(byPerson).map(([name, hrs]) => (
            <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #F1F5F9', fontSize: 13 }}>
              <span style={{ color: '#475569' }}>{name}</span><span style={{ fontWeight: 700 }}>{hrs}h</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── INVOICES ────────────────────────────────────────────────────────────────
function InvoicesTab({ job, isAdmin, updateJob, showToast }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '' });
  const invoices = job.invoices || [];
  const paid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const pend = invoices.filter(i => i.status === 'pending').reduce((s, i) => s + i.amount, 0);

  function create() {
    if (!form.amount) { showToast('Amount required.', 'error'); return; }
    const inv = { id: 'inv-' + Date.now(), date: new Date().toISOString().slice(0, 10), amount: +form.amount, status: 'pending', description: form.description || `Invoice for job`, paid_date: '' };
    updateJob({ invoices: [...invoices, inv] }).then(() => { showToast('Invoice created.'); setShowCreate(false); setForm({ description: '', amount: '' }); });
  }

  function markPaid(invId) {
    const updated = invoices.map(i => i.id === invId ? { ...i, status: 'paid', paid_date: new Date().toISOString().slice(0, 10) } : i);
    const paidAmt = invoices.find(i => i.id === invId)?.amount || 0;
    updateJob({ invoices: updated, amount_paid: (job.amount_paid || 0) + paidAmt }).then(() => showToast('Marked paid! ✓'));
  }

  return (
    <div>
      <Grid4 style={{ marginBottom: '1rem' }}>
        <Stat label="Contract" value={fmtCurrency(job.contract_total)} accent={B.navy} />
        <Stat label="Paid" value={fmtCurrency(paid)} accent="#16A34A" />
        <Stat label="Pending" value={fmtCurrency(pend)} accent={pend > 0 ? '#D97706' : '#16A34A'} />
        <Stat label="Balance" value={fmtCurrency((job.contract_total || 0) - paid)} accent={(job.contract_total || 0) - paid > 0 ? '#DC2626' : '#16A34A'} />
      </Grid4>
      <Card>
        <SH title="Invoices" action={isAdmin && <Btn size="sm" variant="primary" onClick={() => setShowCreate(!showCreate)}>+ Create</Btn>} />
        {showCreate && (
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Description"><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. 50% deposit" /></Field>
              <Field label="Amount ($) *"><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></Field>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}><Btn size="sm" variant="primary" onClick={create}>Create</Btn><Btn size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Btn></div>
          </div>
        )}
        {invoices.length === 0 && <p style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: '2rem' }}>No invoices yet.</p>}
        {invoices.map(inv => (
          <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: `1px solid ${inv.status === 'paid' ? '#86EFAC' : '#FDE68A'}`, borderRadius: 10, background: inv.status === 'paid' ? '#F0FDF4' : '#FFFBEB', marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{inv.description || inv.id}</div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>Issued: {fmtDate(inv.date)}{inv.paid_date ? ` · Paid: ${fmtDate(inv.paid_date)}` : ''}</div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif", color: inv.status === 'paid' ? '#16A34A' : '#D97706' }}>{fmtCurrency(inv.amount)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: inv.status === 'paid' ? '#DCFCE7' : '#FEF9C3', color: inv.status === 'paid' ? '#15803D' : '#B45309' }}>{inv.status === 'paid' ? 'PAID' : 'PENDING'}</span>
              {isAdmin && inv.status === 'pending' && <Btn size="sm" variant="success" onClick={() => markPaid(inv.id)}>Mark Paid</Btn>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── PHOTOS ──────────────────────────────────────────────────────────────────
function PhotosTab({ job, isAdmin, updateJob, showToast }) {
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [phase, setPhase] = useState('');
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef();

  const photos = job.photos || [];

  async function onFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setPreview({ dataUrl: ev.target.result, file });
    reader.readAsDataURL(file);
  }

  async function upload() {
    if (!preview) return;
    setUploading(true);
    try {
      // Upload to Supabase Storage
      const ext = preview.file.name.split('.').pop();
      const path = `jobs/${job.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('job-photos').upload(path, preview.file, { upsert: false });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from('job-photos').getPublicUrl(path);
      const photo = { id: 'p' + Date.now(), name: caption || preview.file.name, date: new Date().toISOString().slice(0, 10), phase: phase || 'General', url: publicUrl, path };
      await updateJob({ photos: [...photos, photo] });
      setPreview(null); setCaption(''); setPhase('');
      showToast('Photo uploaded! 📷');
    } catch (err) {
      showToast('Upload failed: ' + err.message, 'error');
    }
    setUploading(false);
  }

  async function deletePhoto(photo) {
    if (!window.confirm('Delete this photo?')) return;
    if (photo.path) await supabase.storage.from('job-photos').remove([photo.path]);
    await updateJob({ photos: photos.filter(p => p.id !== photo.id) });
    setSelected(null);
    showToast('Photo deleted.');
  }

  return (
    <div>
      {isAdmin && (
        <Card style={{ marginBottom: '1rem' }}>
          <SH title="Upload Photo" />
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }} onChange={onFile} />
              <Btn variant="outline" onClick={() => fileRef.current?.click()}>📷 Choose Photo</Btn>
            </div>
            {preview && (
              <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap' }}>
                <img src={preview.dataUrl} alt="preview" style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 8, border: '1px solid #E2E8F0' }} />
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Field label="Caption"><Input value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Front slope complete" /></Field>
                  <Field label="Phase"><Input value={phase} onChange={e => setPhase(e.target.value)} placeholder="e.g. Shingles" /></Field>
                  <Btn variant="primary" size="sm" onClick={upload} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</Btn>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
      <Card>
        <SH title={`Photos (${photos.length})`} />
        {photos.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }}>No photos yet.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10 }}>
          {photos.map(p => (
            <div key={p.id} onClick={() => setSelected(p)} style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid #E2E8F0', transition: 'box-shadow .15s' }} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.1)'} onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
              {p.url ? <img src={p.url} alt={p.name} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} /> : <div style={{ height: 90, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>📷</div>}
              <div style={{ padding: '5px 7px', background: '#fff' }}>
                <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>{p.phase} · {fmtDate(p.date)}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)}>
          {selected.url ? <img src={selected.url} alt={selected.name} style={{ width: '100%', borderRadius: 8, maxHeight: 400, objectFit: 'contain' }} /> : <div style={{ height: 200, background: '#F1F5F9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 60 }}>📷</div>}
          <div style={{ marginTop: 10, fontSize: 13, color: '#64748B' }}>Phase: {selected.phase} · {fmtDate(selected.date)}</div>
          {isAdmin && <div style={{ marginTop: 12 }}><Btn variant="danger" size="sm" onClick={() => deletePhoto(selected)}>Delete Photo</Btn></div>}
        </Modal>
      )}
    </div>
  );
}

// ─── MESSAGES ────────────────────────────────────────────────────────────────
function MessagesTab({ job, isAdmin, user, profile, updateJob, showToast }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();
  const messages = job.messages || [];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  async function send() {
    if (!text.trim()) return;
    setSending(true);
    const msg = { id: 'msg' + Date.now(), from: isAdmin ? 'admin' : 'customer', sender_id: user?.id, sender_name: profile?.full_name || profile?.email || 'You', text: text.trim(), time: new Date().toISOString() };
    await updateJob({ messages: [...messages, msg] });
    setText('');
    setSending(false);
  }

  return (
    <Card style={{ display: 'flex', flexDirection: 'column' }}>
      <SH title={`Messages (${messages.length})`} />
      <div style={{ height: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0', marginBottom: 12 }}>
        {messages.map(m => {
          const isMe = m.sender_id === user?.id || (isAdmin && m.from === 'admin');
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', gap: 8, alignItems: 'flex-end' }}>
              {!isMe && <Avatar name={m.sender_name} size={28} color={m.from === 'admin' ? B.navy : B.amber} />}
              <div style={{ maxWidth: '72%' }}>
                {!isMe && <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 3 }}>{m.sender_name}</div>}
                <div style={{ padding: '10px 14px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px', background: isMe ? B.navy : '#F1F5F9', color: isMe ? '#fff' : B.navy, fontSize: 13, lineHeight: 1.5 }}>{m.text}</div>
                <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 3, textAlign: isMe ? 'right' : 'left' }}>{fmtTime(m.time)}</div>
              </div>
              {isMe && <Avatar name={m.sender_name} size={28} color={isAdmin ? B.navy : B.amber} />}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '1rem', display: 'flex', gap: 8 }}>
        <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) send(); }} placeholder="Type a message…" />
        <Btn variant="dark" onClick={send} disabled={sending || !text.trim()}>Send →</Btn>
      </div>
    </Card>
  );
}
